import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import fiatPurchaseService from '../../services/fiatPurchaseService';
import RequestConfirmationModal from './RequestConfirmationModal';
import TransactionHistory from './TransactionHistory';

const BuyFromFiat = () => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('bank');
  const [sellAmount, setSellAmount] = useState('');
  const [buyAmount, setBuyAmount] = useState('');
  const [error, setError] = useState('');
  const [btc1Price, setBtc1Price] = useState(null);
  const [fiatRates, setFiatRates] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [requestData, setRequestData] = useState(null);

  const fiatCurrencies = [
    { symbol: 'USD', name: 'US Dollar', icon: '$' },
    { symbol: 'GBP', name: 'British Pound', icon: '£' },
    { symbol: 'EUR', name: 'Euro', icon: '€' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchBTC1Price(),
        fetchFiatRates(),
        fetchPaymentMethods()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate when fiatRates or selectedCurrency changes
  useEffect(() => {
    if (Object.keys(fiatRates).length > 0 && selectedCurrency) {
      if (sellAmount && sellAmount !== '') {
        const sellNum = parseFloat(sellAmount);
        if (!isNaN(sellNum) && sellNum > 0) {
          const fiatToUsdRate = fiatRates[selectedCurrency] || getStaticPrice(selectedCurrency);
          const currentBtc1Price = btc1Price || 108;
          
          if (fiatToUsdRate && currentBtc1Price) {
            const sellValueInUSD = sellNum / fiatToUsdRate;
            const btc1Amount = sellValueInUSD / currentBtc1Price;
            setBuyAmount(btc1Amount.toFixed(6));
          }
        }
      } else if (buyAmount && buyAmount !== '') {
        const buyNum = parseFloat(buyAmount);
        if (!isNaN(buyNum) && buyNum > 0) {
          const fiatToUsdRate = fiatRates[selectedCurrency] || getStaticPrice(selectedCurrency);
          const currentBtc1Price = btc1Price || 108;
          
          if (fiatToUsdRate && currentBtc1Price) {
            const buyValueInUSD = buyNum * currentBtc1Price;
            const sellAmount = buyValueInUSD * fiatToUsdRate;
            setSellAmount(sellAmount.toFixed(2));
          }
        }
      }
    }
  }, [fiatRates, selectedCurrency, btc1Price]);

  const fetchBTC1Price = async () => {
    try {
      // Get the custom BTC1 price from the quick swap API
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
      const btc1PriceResponse = await fetch(`${baseUrl}/v1/quick-swap/btc1-price/`);
      
      if (btc1PriceResponse.ok) {
        const btc1PriceData = await btc1PriceResponse.json();
        if (btc1PriceData.price) {
          setBtc1Price(btc1PriceData.price);
          console.log(`BTC1 price from quick swap API: ${btc1PriceData.price}`);
        } else {
          setBtc1Price(108.34);
          console.log('No BTC1 price in response, using fallback');
        }
      } else {
        console.warn('Failed to fetch BTC1 price from API, using fallback');
        setBtc1Price(108.34);
      }
      
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error fetching BTC1 price:', err);
      // Use fallback values on API error
      setBtc1Price(108.34);
      setError(''); // Clear error since we have a fallback
    }
  };

  const fetchFiatRates = async () => {
    try {
      const rates = await fiatPurchaseService.getFiatRates();
      setFiatRates(rates);
      console.log('Fiat rates loaded:', rates);
    } catch (err) {
      console.error('Error fetching fiat rates:', err);
      // Use static fallback rates
      setFiatRates({
        USD: 1,
        GBP: 0.79,
        EUR: 0.92
      });
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const methods = await fiatPurchaseService.getPaymentMethods();
      setPaymentMethods(methods);
      console.log('Payment methods loaded:', methods);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      // Use static fallback methods
      setPaymentMethods([
        { id: 'bank', name: 'Bank Transfer', description: 'Direct bank transfer', icon: '🏦', processing_time: '1-3 business days' },
        { id: 'wire', name: 'Wire Transfer', description: 'International wire transfer', icon: '💳', processing_time: '2-5 business days' },
        { id: 'credit_card', name: 'Credit Card', description: 'Visa, Mastercard, American Express', icon: '💳', processing_time: 'Instant' },
        { id: 'paypal', name: 'PayPal', description: 'PayPal payment processing', icon: '💰', processing_time: 'Instant' },
        { id: 'moonpay', name: 'MoonPay', description: 'Crypto payment gateway', icon: '🌙', processing_time: '5-30 minutes' }
      ]);
    }
  };

  // Get current fiat rate or fallback to static price
  const getStaticPrice = (currency) => {
    // First try to get real-time rate from API
    if (fiatRates[currency]) {
      return fiatRates[currency];
    }
    
    // Fallback to static prices
    const staticPrices = {
      USD: 1,
      GBP: 0.79,
      EUR: 0.92,
      
    };
    return staticPrices[currency] || 1;
  };

  // Format large numbers with commas
  const formatLargeNumber = (num) => {
    if (!num || num === '') return '';
    
    // Preserve the original string format if it's zero with decimals (e.g., "0.000")
    const number = parseFloat(num);
    if (isNaN(number)) {
      // If it's not a valid number but has content, return as-is (might be partial input like "0.")
      return num;
    }
    
    // If the number is zero and the original string contains a decimal point, preserve the format
    if (number === 0 && typeof num === 'string' && num.includes('.')) {
      // Extract the decimal part
      const parts = num.split('.');
      const decimalPart = parts[1] || '';
      // Preserve the original decimal places (up to 6)
      const preservedDecimals = decimalPart.substring(0, 6);
      // Return with preserved decimal places
      return `0.${preservedDecimals}`;
    }

    // For very large numbers, show without decimals
    if (number >= 1000000) {
      return number.toLocaleString('en-US', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      });
    }

    // For smaller numbers, show with appropriate decimals
    return number.toLocaleString('en-US', {
      maximumFractionDigits: 6,
      minimumFractionDigits: 0
    });
  };

  // Calculate buy amount when sell amount changes
  const calculateBuyAmount = (sellValue) => {
    if (!sellValue || !selectedCurrency) return '';
    const sellNum = parseFloat(sellValue);
    if (isNaN(sellNum) || sellNum <= 0) return '';
    
    // Use fiat exchange rate to convert to USD
    const fiatToUsdRate = fiatRates[selectedCurrency] || getStaticPrice(selectedCurrency);
    const currentBtc1Price = btc1Price || 108;
    
    if (!fiatToUsdRate || !currentBtc1Price) return '';
    
    // Convert fiat amount to USD, then calculate BTC1 amount
    const sellValueInUSD = sellNum / fiatToUsdRate;
    const btc1Amount = sellValueInUSD / currentBtc1Price;
    return btc1Amount.toFixed(6);
  };

  // Calculate sell amount when buy amount changes
  const calculateSellAmount = (buyValue) => {
    if (!buyValue || !selectedCurrency) return '';
    const buyNum = parseFloat(buyValue);
    if (isNaN(buyNum) || buyNum <= 0) return '';
    
    // Use fiat exchange rate to convert from USD
    const fiatToUsdRate = fiatRates[selectedCurrency] || getStaticPrice(selectedCurrency);
    const currentBtc1Price = btc1Price || 108;
    
    if (!fiatToUsdRate || !currentBtc1Price) return '';
    
    // Convert BTC1 amount to USD, then convert to fiat currency
    const buyValueInUSD = buyNum * currentBtc1Price;
    const sellAmount = buyValueInUSD * fiatToUsdRate;
    return sellAmount.toFixed(2);
  };

  const handleSellAmountChange = (value) => {
    setSellAmount(value);
    setBuyAmount(calculateBuyAmount(value));
  };

  const handleBuyAmountChange = (value) => {
    setBuyAmount(value);
    setSellAmount(calculateSellAmount(value));
  };

  const handleCurrencyChange = (newCurrency) => {
    setSelectedCurrency(newCurrency);
    
    if (sellAmount && sellAmount !== '') {
      const sellNum = parseFloat(sellAmount);
      if (!isNaN(sellNum) && sellNum > 0) {
        const fiatToUsdRate = fiatRates[newCurrency] || getStaticPrice(newCurrency);
        const currentBtc1Price = btc1Price || 108;
        
        if (fiatToUsdRate && currentBtc1Price) {
          const sellValueInUSD = sellNum / fiatToUsdRate;
          const btc1Amount = sellValueInUSD / currentBtc1Price;
          setBuyAmount(btc1Amount.toFixed(6));
        }
      }
    } else if (buyAmount && buyAmount !== '') {
      const buyNum = parseFloat(buyAmount);
      if (!isNaN(buyNum) && buyNum > 0) {
        const fiatToUsdRate = fiatRates[newCurrency] || getStaticPrice(newCurrency);
        const currentBtc1Price = btc1Price || 108;
        
        if (fiatToUsdRate && currentBtc1Price) {
          const buyValueInUSD = buyNum * currentBtc1Price;
          const sellAmount = buyValueInUSD * fiatToUsdRate;
          setSellAmount(sellAmount.toFixed(2));
        }
      }
    }
  };

  const handleSwap = async () => {
    if (!selectedCurrency || !sellAmount || parseFloat(sellAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!selectedPaymentMethod) {
      setError('Please select a payment method');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const requestData = {
        fiat_currency: selectedCurrency,
        fiat_amount: parseFloat(sellAmount),
        btc1_amount: parseFloat(buyAmount),
        btc1_price: btc1Price,
        payment_method: selectedPaymentMethod,
        user_notes: `Purchase request for ${sellAmount} ${selectedCurrency} via ${selectedPaymentMethod}`
      };

      const response = await fiatPurchaseService.submitPurchaseRequest(requestData);
      
      if (response.success) {
        // Store request data for modal
        setRequestData({
          ...requestData,
          request_id: response.request_id,
          admin_email: response.admin_email,
          estimated_response_time: response.estimated_response_time,
          email_sent: response.email_sent,
          warning: response.warning
        });
        
        // Show appropriate success message
        if (response.email_sent) {
          toast.success('Purchase request submitted successfully!', {
            icon: '✅',
          });
        } else {
          toast.success('Request submitted! (Email notification failed)', {
            icon: '⚠️',
          });
        }
        
        // Show confirmation modal
        setShowConfirmationModal(true);
        
        // Reset form
        setSellAmount('');
        setBuyAmount('');
      } else {
        throw new Error(response.error || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting purchase request:', error);
      
      // Handle different error response formats
      let errorMessage = 'Failed to submit purchase request';
      
      if (error && typeof error === 'object') {
        if (error.error) {
          errorMessage = error.error;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.detail) {
          errorMessage = error.detail;
        } else if (error.non_field_errors && Array.isArray(error.non_field_errors)) {
          errorMessage = error.non_field_errors[0];
        } else {
          // Try to extract error from nested objects
          const firstError = Object.values(error)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0]?.message || firstError[0] || 'Failed to submit purchase request';
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      setError(errorMessage);
      toast.error(errorMessage, {
        icon: '❌',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrencyIcon = (currency) => {
    const fiat = fiatCurrencies.find(f => f.symbol === currency);
    return fiat ? fiat.icon : currency;
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="loading-spinner mx-auto mb-4"></div>
        <p className="text-white">Loading payment methods and rates...</p>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-8 mt-12 sm:mt-0">
      <div className="max-w-md sm:max-w-lg lg:max-w-3xl mx-auto bg-gray-900 rounded-lg p-3 sm:p-4 lg:p-6">
        <div className="space-y-3 sm:space-y-4">
          {/* Pay Section */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-5 h-32 sm:h-36 lg:h-44 flex flex-col justify-between">
            <label className="block text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
              You Pay
            </label>
            <div className="flex items-center justify-between mb-2">
              <div className="relative w-full max-w-[200px] sm:max-w-[300px] lg:max-w-[500px]">
                <span className="text-[24px] sm:text-[30px] lg:text-[38px] font-bold text-white bg-transparent block">
                  {formatLargeNumber(sellAmount) || '0'}
                </span>
                <input
                  type="number"
                  value={sellAmount}
                  onChange={(e) => handleSellAmountChange(e.target.value)}
                  placeholder="0"
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  min="0"
                   step="0.000001"
                />
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-600 rounded-lg p-1.5 sm:p-2 relative z-50 min-w-[80px] sm:min-w-[100px]">
                <select
                  value={selectedCurrency}
                  onChange={(e) => handleCurrencyChange(e.target.value)}
                  className="bg-transparent text-white border-none focus:outline-none text-xs sm:text-sm font-medium appearance-none cursor-pointer pr-4 sm:pr-6 z-50 w-full"
                  style={{ zIndex: 9999 }}
                >
                  {fiatCurrencies.map((fiat) => (
                    <option key={fiat.symbol} value={fiat.symbol} className="bg-gray-800 text-white text-xs sm:text-sm py-1 px-2">
                      {fiat.symbol} - {fiat.name}
                    </option>
                  ))}
                </select>
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white absolute right-1 sm:right-2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-white">
              <span className="truncate">${sellAmount ? (parseFloat(sellAmount) / (fiatRates[selectedCurrency] || getStaticPrice(selectedCurrency))).toFixed(2) : '0'}</span>
              <span className="text-white truncate ml-2">{sellAmount || '0'} {selectedCurrency}</span>
            </div>
          </div>

          {/* Swap Arrow */}
          <div className="flex justify-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#0E1696] !text-white rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 !text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </div>
          </div>

          {/* Receive Section */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-5 h-32 sm:h-36 lg:h-44 flex flex-col justify-between">
            <label className="block text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
              You Receive
            </label>
            <div className="flex items-center justify-between mb-2">
              <div className="relative w-full max-w-[200px] sm:max-w-[300px] lg:max-w-[500px]">
                <span className="text-[24px] sm:text-[30px] lg:text-[38px] font-bold text-white bg-transparent block">
                  {formatLargeNumber(buyAmount) || '0'}
                </span>
                <input
                  type="number"
                  value={buyAmount}
                  onChange={(e) => handleBuyAmountChange(e.target.value)}
                  placeholder="0"
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  min="0"
                  step="0.000001"
                />
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-600 rounded-lg px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 min-w-[80px] sm:min-w-[100px]">
                <span className="text-sm sm:text-lg text-white">₿</span>
                <span className="text-white text-sm sm:text-lg font-medium ml-1 sm:ml-2">BTC1</span>
              </div>
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-white">
              <span className="truncate">${buyAmount ? (parseFloat(buyAmount) * (btc1Price)).toFixed(2) : '0'}</span>
              <span className="text-white truncate ml-2">${btc1Price} per BTC1</span>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className='mt-3 sm:mt-4 mb-2'>
            <label className="block text-xs sm:text-sm font-medium text-white mb-2">
              Payment Method
            </label>
            {loading ? (
              <div className="text-center text-white py-4">
                Loading payment methods...
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {paymentMethods.map((method) => (
                  <label
                    key={method.id}
                    className={`flex flex-col items-center p-2 sm:p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 border-2 transition-all ${
                      selectedPaymentMethod === method.id
                        ? 'border-[#2A356D] bg-gray-700'
                        : 'border-transparent'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      checked={selectedPaymentMethod === method.id}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="sr-only"
                    />
                    <span className="text-lg sm:text-xl mb-1">{method.icon}</span>
                    <div className="text-center">
                      <div className="text-white font-medium text-xs sm:text-sm">{method.name}</div>
                      <div className="text-xs text-white mt-0.5">{method.processing_time}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Selected Payment Method Info */}
          {selectedPaymentMethod && (
            <div className="bg-yellow-900/20 border border-[#2A356D] rounded-lg p-2 sm:p-3 my-2">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-white text-xs sm:text-sm font-medium">
                  Selected: {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name} • 
                  Processing: {paymentMethods.find(m => m.id === selectedPaymentMethod)?.processing_time}
                </span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-2 sm:p-3">
              <p className="text-red-400 text-xs sm:text-sm">{error}</p>
            </div>
          )}

          {/* Buy Button */}
          <button
            onClick={handleSwap}
            disabled={!selectedCurrency || !sellAmount || parseFloat(sellAmount) <= 0 || !selectedPaymentMethod || isSubmitting}
            className="w-full bg-[#0E1696] hover:bg-[#0E1696] !text-white py-3 sm:py-4 mt-3 sm:mt-4 text-sm sm:text-lg font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isSubmitting ? 'Submitting Request...' : `Submit Request`}
          </button>
        </div>
      </div>

      {/* Transaction History Section */}
      <div className="mt-8">
        <TransactionHistory type="fiat_request" limit={5} showTitle={true} />
      </div>

      {/* Request Confirmation Modal */}
      <RequestConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        requestData={requestData}
      />
    </div>
  );
};

export default BuyFromFiat;